import { newError } from '#app/lib/error'
import { looksLikeAnIsbn, normalizeIsbn } from '#app/lib/isbn'
import { getWdUriFromWikimediaLanguageCode } from '#app/lib/languages/languages_indexes'
import { buildPath } from '#app/lib/location'
import { treq } from '#app/lib/treq'
import { getEntityPropValue } from '#entities/components/lib/claims_helpers'
import { getEntityByUri, type SerializedEntity } from '#entities/lib/entities'
import type { EntityUri } from '#server/types/entity'
import { getCurrentLang, I18n, i18n } from '#user/lib/i18n'
import { resolveAndGetEditionUri } from '../editor/lib/create_helpers'

export async function createEditionFromWork (params) {
  const { workEntity, userInput } = params

  const isbn = normalizeIsbn(userInput)

  await rejectCreatingAnEditionWithAUsedIsbn(isbn, workEntity)

  const uri = await resolveAndGetEditionUri({
    edition: { isbn },
    works: [ { uri: workEntity.uri } ],
  })
  return getEntityByUri({ uri })
}

async function rejectCreatingAnEditionWithAUsedIsbn (isbn: string, workEntity: SerializedEntity) {
  try {
    const existingEntity = await getEntityByUri({ uri: `isbn:${isbn}` })
    if (existingEntity) {
      reportIsbnIssue(workEntity.uri, isbn)
      throw duplicatedWorkError(workEntity.uri, isbn)
    }
  } catch (err) {
    if (err.statusCode !== 404) throw err
  }
}

async function reportIsbnIssue (workUri: EntityUri, isbn: string) {
  const params = { uri: workUri, isbn }
  return treq('tasks', 'post', 'deduplicate-works', params)
}

function duplicatedWorkError (workUri: EntityUri, isbn: string) {
  const normalizedIsbn = normalizeIsbn(isbn)
  const alreadyExist = I18n('this ISBN already exist:')
  const err = newError(alreadyExist, 400, { isbn, workUri })
  const link = `<a href='/entity/isbn:${normalizedIsbn}' class='showEntity'>${normalizedIsbn}</a>`
  const reported = i18n('the issue was reported')
  err.html = `${alreadyExist} ${link} (${reported})`
  return err
}

export function validateEditionPossibility (userInput: string, editions: SerializedEntity[]) {
  if (!looksLikeAnIsbn(userInput)) {
    throw newError(`invalid isbn: ${userInput}`, 400, { userInput, editions })
  }
  const isbn = normalizeIsbn(userInput)
  if (editions.map(getNormalizedIsbn).includes(isbn)) {
    throw newError('this edition is already in the list', 400, { isbn, editions })
  }
}

function getNormalizedIsbn (edition: SerializedEntity) {
  const isbn = getEntityPropValue(edition, 'wdt:P212')
  if (isbn) return normalizeIsbn(isbn)
}

export async function getAddWithoutIsbnPath (work: SerializedEntity) {
  return buildPath('/entity/new', await workEditionCreationData(work))
}

async function workEditionCreationData (work: SerializedEntity) {
  const data = {
    type: 'edition',
    claims: {
      'wdt:P629': [ work.uri ],
    },
  }
  const lang = getCurrentLang()
  const langWdUri = await getWdUriFromWikimediaLanguageCode(lang)
  // Suggest user's language as edition language
  if (langWdUri) data.claims['wdt:P407'] = [ langWdUri ]

  // Suggest work label in user's language as edition title
  const { label } = work
  if (label) data.claims['wdt:P1476'] = [ label ]

  return data
}
